import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";

// Import WebM videos (알파채널 없음 - iOS 호환)
import cardAction1Video from "../../assets/animations/cardAction1.webm";
import cardAction2Video from "../../assets/animations/cardAction2.webm";
import cardAction3Video from "../../assets/animations/cardAction3.webm";
import cardAction4Video from "../../assets/animations/cardAction4.webm";
import cardAction5Video from "../../assets/animations/cardAction5.webm";

interface DesignSectionProps {}

export const DesignSection: React.FC<DesignSectionProps> = () => {
  const [activeScrollDot, setActiveScrollDot] = useState(0);
  const horizontalScrollRef = useRef<HTMLDivElement>(null);
  const cardRef1 = useRef<HTMLDivElement>(null);
  const cardRef2 = useRef<HTMLDivElement>(null);
  const cardRef3 = useRef<HTMLDivElement>(null);
  const designSectionRef = useRef<HTMLDivElement>(null);
  const [isSectionVisible, setIsSectionVisible] = useState(false);
  const [cardWidth, setCardWidth] = useState(908); // 기본값: 900px + 8px gap

  // Video refs
  const video1Ref = useRef<HTMLVideoElement>(null);
  const video2Ref = useRef<HTMLVideoElement>(null);
  const video3Ref = useRef<HTMLVideoElement>(null);
  const video4Ref = useRef<HTMLVideoElement>(null);
  const video5Ref = useRef<HTMLVideoElement>(null);

  // 카드 너비 동적으로 계산
  useEffect(() => {
    const updateCardWidth = () => {
      if (cardRef1.current && horizontalScrollRef.current) {
        const card = cardRef1.current;
        const scrollContainer = horizontalScrollRef.current;
        const cardRect = card.getBoundingClientRect();
        const containerStyle = window.getComputedStyle(scrollContainer);
        const gap = parseFloat(containerStyle.gap) || 8;
        setCardWidth(cardRect.width + gap);
      }
    };

    updateCardWidth();
    window.addEventListener("resize", updateCardWidth);
    return () => window.removeEventListener("resize", updateCardWidth);
  }, []);

  // 카드 자동 스크롤 (각 비디오 길이에 맞춰 조정)
  useEffect(() => {
    // 비디오 길이 기반 대기 시간 (각 비디오가 한 번 재생될 시간 + 1초 딜레이)
    const videoWaitTimes = [
      5000, // CardAction1: 약 5초
      4000, // CardAction2: 약 4초
      5000, // CardAction5: 약 5초
      3500, // CardAction4: 약 3.5초
      3000, // CardAction3: 약 3초
    ];

    const waitTime = 1000; // 비디오 재생 후 다음으로 넘어가기 전 1초 딜레이
    const currentWaitTime = videoWaitTimes[activeScrollDot] + waitTime;

    const autoScrollInterval = setInterval(() => {
      if (horizontalScrollRef.current) {
        const currentIndex = activeScrollDot;
        const nextIndex = (currentIndex + 1) % 5;
        scrollToCard(nextIndex);
      }
    }, currentWaitTime);

    return () => clearInterval(autoScrollInterval);
  }, [activeScrollDot]);

  // Intersection Observer로 섹션이 보일 때 감지
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsSectionVisible(true);
          }
        });
      },
      {
        threshold: 0.3, // 섹션의 30%가 보이면 트리거
        rootMargin: "0px 0px -20% 0px", // 화면 중간쯤에서 트리거
      }
    );

    if (designSectionRef.current) {
      observer.observe(designSectionRef.current);
    }

    return () => {
      if (designSectionRef.current) {
        observer.unobserve(designSectionRef.current);
      }
    };
  }, []);

  // 현재 선택된 카드의 비디오 자동 재생 (섹션이 보일 때만)
  useEffect(() => {
    if (!isSectionVisible) return;

    const videoRefs = [video1Ref, video2Ref, video3Ref, video4Ref, video5Ref];

    // 모든 비디오 정지
    videoRefs.forEach((ref) => {
      if (ref.current) {
        ref.current.pause();
        ref.current.currentTime = 0;
      }
    });

    // 현재 활성 비디오만 재생
    const currentVideoRef = videoRefs[activeScrollDot];
    if (currentVideoRef.current) {
      currentVideoRef.current.play().catch((err) => {
        console.log("Video autoplay prevented:", err);
      });
    }
  }, [activeScrollDot, isSectionVisible]);

  // Horizontal scroll detection
  useEffect(() => {
    const handleHorizontalScroll = () => {
      if (horizontalScrollRef.current) {
        const scrollLeft = horizontalScrollRef.current.scrollLeft;
        const activeIndex = Math.round(scrollLeft / cardWidth);
        if (activeIndex !== activeScrollDot) {
          setActiveScrollDot(activeIndex);
        }
      }
    };

    const scrollContainer = horizontalScrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleHorizontalScroll, {
        passive: true,
      });
      return () =>
        scrollContainer.removeEventListener("scroll", handleHorizontalScroll);
    }
  }, [activeScrollDot, cardWidth]);

  const scrollToCard = (index: number) => {
    if (horizontalScrollRef.current) {
      horizontalScrollRef.current.scrollTo({
        left: index * cardWidth,
        behavior: "smooth",
      });
    }
  };

  return (
    <section
      ref={designSectionRef}
      className="py-32 relative overflow-hidden"
      style={{
        background:
          "linear-gradient(to bottom, #ffffff 0%, #f5f5f5 50%, #fcf7faff 100%)",
        maxHeight: "1200px" /* 1400px → 1200px: 높이 조정 */,
      }}
    >
      <div className="container mx-auto px-6 md:px-12 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
            나에게 꼭 맞는 차트
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            진료 워크플로우 최적화 · 자동 커스터마이징 · 큰 글꼴 · 자유로운
            확대/축소
          </p>
          <p className="text-lg md:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            접수, 치료, 진료 모두를 위한 맞춤 인터페이스
          </p>
        </motion.div>
      </div>

      <div className="horizontal-scroll-wrapper">
        <div className="horizontal-scroll-section" ref={horizontalScrollRef}>
          <div className="horizontal-scroll-container">
            <motion.div
              ref={cardRef1}
              className={`feature-card-large ${
                activeScrollDot === 0 ? "active" : ""
              }`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <motion.div
                className={`relative group rounded-2xl p-8 shadow-lg ${
                  activeScrollDot === 0 ? "glass-card-active" : "glass-card"
                }`}
                style={{ overflow: "visible" }}
                transition={{ duration: 0.3 }}
              >
                <div
                  className="relative overflow-hidden rounded-lg video-container"
                  style={{ flexShrink: 0 }}
                >
                  <video
                    ref={video1Ref}
                    src={cardAction1Video}
                    muted
                    playsInline
                    loop
                    preload="auto"
                    style={{
                      display: "block",
                      width: "100%",
                      height: "auto",
                      maxWidth: "796px",
                      objectFit: "contain",
                    }}
                  />
                </div>
                <div className="mt-6">
                  <h3 className="text-xl font-bold mb-2 text-gray-900">
                    워크플로우 기반 대시보드
                  </h3>
                  <p className="text-sm text-gray-600">
                    진료 프로세스에 맞춘 배치로 빠르고 직관적인 차트 작성이
                    가능합니다
                  </p>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              ref={cardRef2}
              className={`feature-card-large ${
                activeScrollDot === 1 ? "active" : ""
              }`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <motion.div
                className={`relative group rounded-2xl p-8 shadow-lg ${
                  activeScrollDot === 1 ? "glass-card-active" : "glass-card"
                }`}
                style={{ overflow: "visible" }}
                transition={{ duration: 0.3 }}
              >
                <div
                  className="relative overflow-hidden rounded-lg video-container"
                  style={{ flexShrink: 0 }}
                >
                  <video
                    ref={video2Ref}
                    src={cardAction2Video}
                    muted
                    playsInline
                    loop
                    preload="auto"
                    style={{
                      display: "block",
                      width: "100%",
                      height: "auto",
                      maxWidth: "796px",
                      objectFit: "contain",
                    }}
                  />
                </div>
                <div className="mt-6">
                  <h3 className="text-xl font-bold mb-2 text-gray-900">
                    커스터마이징 가능한 레이아웃
                  </h3>
                  <p className="text-sm text-gray-600">
                    환경에 맞춰 자유롭게 구성할 수 있는 유연한 인터페이스
                  </p>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              ref={cardRef3}
              className={`feature-card-large ${
                activeScrollDot === 2 ? "active" : ""
              }`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <motion.div
                className={`relative group rounded-2xl p-8 shadow-lg ${
                  activeScrollDot === 2 ? "glass-card-active" : "glass-card"
                }`}
                style={{ overflow: "visible" }}
                transition={{ duration: 0.3 }}
              >
                <div
                  className="relative overflow-hidden rounded-lg video-container"
                  style={{ flexShrink: 0 }}
                >
                  <video
                    ref={video3Ref}
                    src={cardAction5Video}
                    muted
                    playsInline
                    loop
                    preload="auto"
                    style={{
                      display: "block",
                      width: "100%",
                      height: "auto",
                      maxWidth: "796px",
                      objectFit: "contain",
                    }}
                  />
                </div>
                <div className="mt-6">
                  <h3 className="text-xl font-bold mb-2 text-gray-900">
                    한눈에 보이는 진료정보
                  </h3>
                  <p className="text-sm text-gray-600">
                    전체 환자의 핵심진료내용을 파악가능. 메모 기능을 통해
                    빠짐없이 전달
                  </p>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              className={`feature-card-large ${
                activeScrollDot === 3 ? "active" : ""
              }`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <motion.div
                className={`relative group rounded-2xl p-8 shadow-lg ${
                  activeScrollDot === 3 ? "glass-card-active" : "glass-card"
                }`}
                style={{ overflow: "visible" }}
                transition={{ duration: 0.3 }}
              >
                <div
                  className="relative overflow-hidden rounded-lg video-container"
                  style={{ flexShrink: 0 }}
                >
                  <video
                    ref={video4Ref}
                    src={cardAction4Video}
                    muted
                    playsInline
                    loop
                    preload="auto"
                    style={{
                      display: "block",
                      width: "100%",
                      height: "auto",
                      maxWidth: "796px",
                      objectFit: "contain",
                    }}
                  />
                </div>
                <div className="mt-6">
                  <h3 className="text-xl font-bold mb-2 text-gray-900">
                    사용자 자동 맞춤
                  </h3>
                  <p className="text-sm text-gray-600">
                    사용자가 자주 쓰는 경혈, 질병코드가 자동으로 최상단에 위치
                  </p>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              className={`feature-card-large ${
                activeScrollDot === 4 ? "active" : ""
              }`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <motion.div
                className={`relative group rounded-2xl p-8 shadow-lg ${
                  activeScrollDot === 4 ? "glass-card-active" : "glass-card"
                }`}
                style={{ overflow: "visible" }}
                transition={{ duration: 0.3 }}
              >
                <div
                  className="relative overflow-hidden rounded-lg video-container"
                  style={{ flexShrink: 0 }}
                >
                  <video
                    ref={video5Ref}
                    src={cardAction3Video}
                    muted
                    playsInline
                    loop
                    preload="auto"
                    style={{
                      display: "block",
                      width: "100%",
                      height: "auto",
                      maxWidth: "796px",
                      objectFit: "contain",
                    }}
                  />
                </div>
                <div className="mt-6">
                  <h3 className="text-xl font-bold mb-2 text-gray-900">
                    접근성 높은 인터페이스
                  </h3>
                  <p className="text-sm text-gray-600">
                    큰 글꼴과 유연한 확대/축소
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        <div className="scroll-navigation-dots">
          {[0, 1, 2, 3, 4].map((index) => (
            <button
              key={index}
              className={`scroll-dot ${
                activeScrollDot === index ? "active" : ""
              }`}
              onClick={() => scrollToCard(index)}
              aria-label={`카드 ${index + 1}로 이동`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
